<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Page Header -->
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-gray-900">Audit Logs</h1>
        <p class="mt-2 text-sm text-gray-600">
          Track all role and permission changes across your organization
        </p>
      </div>

      <!-- Statistics Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div class="bg-white rounded-lg shadow-sm p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0 bg-blue-500 rounded-md p-3">
              <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div class="ml-5">
              <p class="text-sm font-medium text-gray-500">Total Logs</p>
              <p class="text-2xl font-semibold text-gray-900">{{ stats.total_logs || 0 }}</p>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow-sm p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0 bg-green-500 rounded-md p-3">
              <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <div class="ml-5">
              <p class="text-sm font-medium text-gray-500">Users Involved</p>
              <p class="text-2xl font-semibold text-gray-900">{{ stats.users_involved || 0 }}</p>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow-sm p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0 bg-purple-500 rounded-md p-3">
              <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
            </div>
            <div class="ml-5">
              <p class="text-sm font-medium text-gray-500">Role Changes</p>
              <p class="text-2xl font-semibold text-gray-900">{{ stats.role_changes || 0 }}</p>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow-sm p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0 bg-orange-500 rounded-md p-3">
              <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div class="ml-5">
              <p class="text-sm font-medium text-gray-500">Permission Changes</p>
              <p class="text-2xl font-semibold text-gray-900">{{ stats.permission_changes || 0 }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="lg:grid lg:grid-cols-4 lg:gap-8">
        <!-- Filters Sidebar -->
        <div class="lg:col-span-1">
          <div class="bg-white rounded-lg shadow-sm p-6 sticky top-8">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Filters</h3>

            <!-- Date Range -->
            <div class="mb-6">
              <label class="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
              <div class="space-y-2">
                <input
                  v-model="filters.start_date"
                  type="date"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Start date"
                />
                <input
                  v-model="filters.end_date"
                  type="date"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="End date"
                />
              </div>
            </div>

            <!-- Quick Date Filters -->
            <div class="mb-6">
              <label class="block text-sm font-medium text-gray-700 mb-2">Quick Filters</label>
              <div class="space-y-2">
                <button
                  v-for="quick in quickFilters"
                  :key="quick.value"
                  @click="applyQuickFilter(quick.value)"
                  :class="[
                    'w-full text-left px-3 py-2 text-sm rounded-md transition-colors',
                    filters.days === quick.value
                      ? 'bg-blue-100 text-blue-700 font-medium'
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                  ]"
                >
                  {{ quick.label }}
                </button>
              </div>
            </div>

            <!-- Action Type -->
            <div class="mb-6">
              <label class="block text-sm font-medium text-gray-700 mb-2">Action Type</label>
              <select
                v-model="filters.action"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Actions</option>
                <optgroup label="Role Actions">
                  <option value="role.created">Role Created</option>
                  <option value="role.updated">Role Updated</option>
                  <option value="role.deleted">Role Deleted</option>
                  <option value="role.duplicated">Role Duplicated</option>
                </optgroup>
                <optgroup label="Permission Actions">
                  <option value="permission.granted">Permission Granted</option>
                  <option value="permission.revoked">Permission Revoked</option>
                  <option value="permission.bulk_granted">Bulk Permissions Granted</option>
                  <option value="permission.bulk_revoked">Bulk Permissions Revoked</option>
                  <option value="permission.copied">Permissions Copied</option>
                </optgroup>
                <optgroup label="User Role Actions">
                  <option value="user.role_assigned">Role Assigned to User</option>
                  <option value="user.role_revoked">Role Revoked from User</option>
                  <option value="user.roles_updated">User Roles Updated</option>
                </optgroup>
              </select>
            </div>

            <!-- User Filter -->
            <div class="mb-6">
              <label class="block text-sm font-medium text-gray-700 mb-2">User</label>
              <select
                v-model="filters.user_id"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Users</option>
                <option v-for="user in users" :key="user.id" :value="user.id">
                  {{ user.name }}
                </option>
              </select>
            </div>

            <!-- Resource Type -->
            <div class="mb-6">
              <label class="block text-sm font-medium text-gray-700 mb-2">Resource Type</label>
              <select
                v-model="filters.auditable_type"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Types</option>
                <option value="Role">Role</option>
                <option value="User">User</option>
                <option value="Permission">Permission</option>
              </select>
            </div>

            <!-- Action Buttons -->
            <div class="flex space-x-2">
              <button
                @click="applyFilters"
                class="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Apply
              </button>
              <button
                @click="clearFilters"
                class="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
              >
                Clear
              </button>
            </div>

            <!-- Export Button -->
            <button
              @click="exportLogs"
              class="w-full mt-4 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors flex items-center justify-center"
            >
              <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export Logs
            </button>
          </div>
        </div>

        <!-- Timeline Content -->
        <div class="mt-8 lg:mt-0 lg:col-span-3">
          <!-- Search Bar -->
          <div class="bg-white rounded-lg shadow-sm p-4 mb-6">
            <div class="relative">
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search audit logs..."
                class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
              <svg class="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          <!-- Audit Logs Timeline -->
          <div v-if="filteredLogs.length > 0" class="bg-white rounded-lg shadow-sm">
            <div class="p-6">
              <h3 class="text-lg font-medium text-gray-900 mb-6">
                Activity Timeline
                <span class="text-sm font-normal text-gray-500 ml-2">
                  ({{ filteredLogs.length }} {{ filteredLogs.length === 1 ? 'entry' : 'entries' }})
                </span>
              </h3>

              <!-- Timeline -->
              <div class="flow-root">
                <ul class="-mb-8">
                  <li
                    v-for="(log, index) in paginatedLogs"
                    :key="log.id"
                    class="relative pb-8"
                  >
                    <!-- Timeline line -->
                    <span
                      v-if="index !== paginatedLogs.length - 1"
                      class="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200"
                      aria-hidden="true"
                    ></span>

                    <div class="relative flex items-start space-x-3">
                      <!-- Icon -->
                      <div class="relative">
                        <div
                          :class="[
                            'h-10 w-10 rounded-full flex items-center justify-center ring-8 ring-white',
                            getActionColor(log.action)
                          ]"
                        >
                          <svg class="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path :d="getActionIcon(log.action)" />
                          </svg>
                        </div>
                      </div>

                      <!-- Content -->
                      <div class="min-w-0 flex-1">
                        <div>
                          <div class="flex items-center justify-between">
                            <div class="flex items-center space-x-2">
                              <!-- User Avatar -->
                              <img
                                v-if="log.user?.avatar"
                                :src="log.user.avatar"
                                :alt="log.user?.name"
                                class="h-8 w-8 rounded-full"
                              />
                              <div
                                v-else
                                class="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 font-medium text-sm"
                              >
                                {{ getUserInitials(log.user?.name) }}
                              </div>
                              <div>
                                <p class="text-sm font-medium text-gray-900">
                                  {{ log.user?.name || 'Unknown User' }}
                                </p>
                                <p class="text-xs text-gray-500">
                                  {{ formatDate(log.created_at) }}
                                </p>
                              </div>
                            </div>

                            <!-- Action Badge -->
                            <span
                              :class="[
                                'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                                getActionBadgeColor(log.action)
                              ]"
                            >
                              {{ log.action_label || formatAction(log.action) }}
                            </span>
                          </div>

                          <!-- Action Description -->
                          <div class="mt-2 text-sm text-gray-700">
                            <p>{{ getActionDescription(log) }}</p>
                          </div>

                          <!-- Before/After Preview for Updates -->
                          <div
                            v-if="log.old_values && log.new_values"
                            class="mt-3 bg-gray-50 rounded-md p-3 border border-gray-200"
                          >
                            <p class="text-xs font-medium text-gray-700 mb-2">Changes:</p>
                            <div class="space-y-1">
                              <div
                                v-for="(value, key) in getChanges(log)"
                                :key="key"
                                class="text-xs"
                              >
                                <span class="font-medium text-gray-600">{{ formatKey(key) }}:</span>
                                <span class="text-red-600 line-through ml-1">{{ value.old }}</span>
                                <span class="mx-1">→</span>
                                <span class="text-green-600">{{ value.new }}</span>
                              </div>
                            </div>
                          </div>

                          <!-- Metadata -->
                          <div class="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                            <span class="flex items-center">
                              <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                              </svg>
                              {{ log.ip_address || 'N/A' }}
                            </span>
                            <button
                              @click="showDetails(log)"
                              class="text-blue-600 hover:text-blue-800 font-medium"
                            >
                              View Details
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>

              <!-- Pagination -->
              <div v-if="totalPages > 1" class="mt-8 flex items-center justify-between border-t border-gray-200 pt-6">
                <div class="text-sm text-gray-700">
                  Showing {{ startIndex + 1 }} to {{ Math.min(endIndex, filteredLogs.length) }} of {{ filteredLogs.length }} results
                </div>
                <div class="flex space-x-2">
                  <button
                    @click="currentPage--"
                    :disabled="currentPage === 1"
                    :class="[
                      'px-4 py-2 border rounded-md text-sm font-medium',
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    ]"
                  >
                    Previous
                  </button>
                  <button
                    @click="currentPage++"
                    :disabled="currentPage === totalPages"
                    :class="[
                      'px-4 py-2 border rounded-md text-sm font-medium',
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    ]"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else class="bg-white rounded-lg shadow-sm p-12 text-center">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">No audit logs found</h3>
            <p class="mt-1 text-sm text-gray-500">
              Try adjusting your filters or check back later.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Details Modal -->
    <div
      v-if="selectedLog"
      class="fixed inset-0 z-50 overflow-y-auto"
      @click.self="closeDetails"
    >
      <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <!-- Background overlay -->
        <div class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" @click="closeDetails"></div>

        <!-- Modal panel -->
        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <!-- Header -->
          <div class="bg-white px-6 py-4 border-b border-gray-200">
            <div class="flex items-center justify-between">
              <h3 class="text-lg font-medium text-gray-900">Audit Log Details</h3>
              <button
                @click="closeDetails"
                class="text-gray-400 hover:text-gray-500"
              >
                <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Content -->
          <div class="bg-white px-6 py-4 max-h-[70vh] overflow-y-auto">
            <!-- Basic Info -->
            <div class="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label class="block text-sm font-medium text-gray-500">Action</label>
                <p class="mt-1 text-sm text-gray-900">{{ selectedLog.action_label || formatAction(selectedLog.action) }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-500">Timestamp</label>
                <p class="mt-1 text-sm text-gray-900">{{ formatDate(selectedLog.created_at) }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-500">User</label>
                <p class="mt-1 text-sm text-gray-900">{{ selectedLog.user?.name || 'Unknown' }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-500">IP Address</label>
                <p class="mt-1 text-sm text-gray-900">{{ selectedLog.ip_address || 'N/A' }}</p>
              </div>
              <div class="col-span-2">
                <label class="block text-sm font-medium text-gray-500">User Agent</label>
                <p class="mt-1 text-sm text-gray-900 break-all">{{ selectedLog.user_agent || 'N/A' }}</p>
              </div>
            </div>

            <!-- Before/After Comparison -->
            <div v-if="selectedLog.old_values || selectedLog.new_values" class="space-y-4">
              <h4 class="text-sm font-medium text-gray-900">Data Changes</h4>

              <div class="grid grid-cols-2 gap-4">
                <!-- Before -->
                <div v-if="selectedLog.old_values">
                  <label class="block text-sm font-medium text-red-600 mb-2">Before</label>
                  <div class="bg-red-50 border border-red-200 rounded-md p-4">
                    <pre class="text-xs text-gray-800 whitespace-pre-wrap">{{ formatJSON(selectedLog.old_values) }}</pre>
                  </div>
                </div>

                <!-- After -->
                <div v-if="selectedLog.new_values">
                  <label class="block text-sm font-medium text-green-600 mb-2">After</label>
                  <div class="bg-green-50 border border-green-200 rounded-md p-4">
                    <pre class="text-xs text-gray-800 whitespace-pre-wrap">{{ formatJSON(selectedLog.new_values) }}</pre>
                  </div>
                </div>
              </div>

              <!-- Diff View -->
              <div v-if="selectedLog.old_values && selectedLog.new_values" class="mt-4">
                <label class="block text-sm font-medium text-gray-900 mb-2">Changes Summary</label>
                <div class="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <div
                    v-for="(change, key) in getDiffChanges(selectedLog)"
                    :key="key"
                    class="mb-2 last:mb-0"
                  >
                    <span class="font-medium text-gray-700">{{ formatKey(key) }}:</span>
                    <div class="ml-4 mt-1">
                      <div class="text-red-600">
                        <span class="font-medium">- </span>{{ change.old }}
                      </div>
                      <div class="text-green-600">
                        <span class="font-medium">+ </span>{{ change.new }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Single Value Display -->
            <div v-else-if="selectedLog.old_values || selectedLog.new_values">
              <h4 class="text-sm font-medium text-gray-900 mb-2">Data</h4>
              <div class="bg-gray-50 border border-gray-200 rounded-md p-4">
                <pre class="text-xs text-gray-800 whitespace-pre-wrap">{{ formatJSON(selectedLog.old_values || selectedLog.new_values) }}</pre>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="bg-gray-50 px-6 py-4 flex justify-end">
            <button
              @click="closeDetails"
              class="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { router } from '@inertiajs/vue3';
import { useToast } from '@/Composables/useToast';

// Props
const props = defineProps({
  logs: {
    type: Array,
    required: true,
    default: () => [],
  },
  users: {
    type: Array,
    required: true,
    default: () => [],
  },
  stats: {
    type: Object,
    default: () => ({}),
  },
  filters: {
    type: Object,
    default: () => ({}),
  },
});

// Composables
const toast = useToast();

// State
const filters = ref({
  start_date: props.filters.start_date || '',
  end_date: props.filters.end_date || '',
  action: props.filters.action || '',
  user_id: props.filters.user_id || '',
  auditable_type: props.filters.auditable_type || '',
  days: props.filters.days || null,
});

const searchQuery = ref('');
const selectedLog = ref(null);
const currentPage = ref(1);
const perPage = ref(15);

// Quick filters
const quickFilters = [
  { label: 'Today', value: 1 },
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
];

// Computed
const filteredLogs = computed(() => {
  let logs = props.logs;

  // Search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    logs = logs.filter(log => {
      return (
        log.action?.toLowerCase().includes(query) ||
        log.user?.name?.toLowerCase().includes(query) ||
        log.action_label?.toLowerCase().includes(query) ||
        log.ip_address?.toLowerCase().includes(query)
      );
    });
  }

  return logs;
});

const totalPages = computed(() => Math.ceil(filteredLogs.value.length / perPage.value));

const startIndex = computed(() => (currentPage.value - 1) * perPage.value);
const endIndex = computed(() => startIndex.value + perPage.value);

const paginatedLogs = computed(() => {
  return filteredLogs.value.slice(startIndex.value, endIndex.value);
});

// Methods
const applyQuickFilter = (days) => {
  filters.value.days = days;
  filters.value.start_date = '';
  filters.value.end_date = '';
  applyFilters();
};

const applyFilters = () => {
  router.get(route('audit-logs.index'), filters.value, {
    preserveState: true,
    preserveScroll: true,
  });
};

const clearFilters = () => {
  filters.value = {
    start_date: '',
    end_date: '',
    action: '',
    user_id: '',
    auditable_type: '',
    days: null,
  };
  searchQuery.value = '';
  applyFilters();
};

const showDetails = (log) => {
  selectedLog.value = log;
};

const closeDetails = () => {
  selectedLog.value = null;
};

const exportLogs = () => {
  // TODO: Implement export functionality
  toast.success('Export functionality coming soon!');
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatAction = (action) => {
  return action
    .split('.')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const formatKey = (key) => {
  return key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const formatJSON = (data) => {
  return JSON.stringify(data, null, 2);
};

const getUserInitials = (name) => {
  if (!name) return '?';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const getActionColor = (action) => {
  if (action?.includes('created') || action?.includes('granted') || action?.includes('assigned')) {
    return 'bg-green-500';
  }
  if (action?.includes('deleted') || action?.includes('revoked')) {
    return 'bg-red-500';
  }
  if (action?.includes('updated')) {
    return 'bg-blue-500';
  }
  if (action?.includes('duplicated') || action?.includes('copied')) {
    return 'bg-purple-500';
  }
  return 'bg-gray-500';
};

const getActionBadgeColor = (action) => {
  if (action?.includes('created') || action?.includes('granted') || action?.includes('assigned')) {
    return 'bg-green-100 text-green-800';
  }
  if (action?.includes('deleted') || action?.includes('revoked')) {
    return 'bg-red-100 text-red-800';
  }
  if (action?.includes('updated')) {
    return 'bg-blue-100 text-blue-800';
  }
  if (action?.includes('duplicated') || action?.includes('copied')) {
    return 'bg-purple-100 text-purple-800';
  }
  return 'bg-gray-100 text-gray-800';
};

const getActionIcon = (action) => {
  if (action?.includes('created') || action?.includes('granted') || action?.includes('assigned')) {
    return 'M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z';
  }
  if (action?.includes('deleted') || action?.includes('revoked')) {
    return 'M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z';
  }
  if (action?.includes('updated')) {
    return 'M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V8a2 2 0 00-2-2h-5L9 4H4zm7 5a1 1 0 10-2 0v1H8a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V9z';
  }
  return 'M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z';
};

const getActionDescription = (log) => {
  const action = log.action;
  const userName = log.user?.name || 'Someone';

  if (action === 'role.created') {
    return `${userName} created a new role`;
  }
  if (action === 'role.updated') {
    return `${userName} updated role settings`;
  }
  if (action === 'role.deleted') {
    return `${userName} deleted a role`;
  }
  if (action === 'role.duplicated') {
    return `${userName} duplicated a role`;
  }
  if (action === 'permission.granted') {
    return `${userName} granted a permission to a role`;
  }
  if (action === 'permission.revoked') {
    return `${userName} revoked a permission from a role`;
  }
  if (action === 'permission.bulk_granted') {
    return `${userName} granted multiple permissions to a role`;
  }
  if (action === 'permission.bulk_revoked') {
    return `${userName} revoked multiple permissions from a role`;
  }
  if (action === 'permission.copied') {
    return `${userName} copied permissions between roles`;
  }
  if (action === 'user.role_assigned') {
    return `${userName} assigned a role to a user`;
  }
  if (action === 'user.role_revoked') {
    return `${userName} revoked a role from a user`;
  }
  if (action === 'user.roles_updated') {
    return `${userName} updated user's roles`;
  }

  return `${userName} performed ${formatAction(action)}`;
};

const getChanges = (log) => {
  if (!log.old_values || !log.new_values) return {};

  const changes = {};
  const oldValues = log.old_values;
  const newValues = log.new_values;

  // Compare and get changes
  for (const key in newValues) {
    if (oldValues[key] !== undefined && JSON.stringify(oldValues[key]) !== JSON.stringify(newValues[key])) {
      changes[key] = {
        old: formatValue(oldValues[key]),
        new: formatValue(newValues[key]),
      };
    }
  }

  return changes;
};

const getDiffChanges = (log) => {
  return getChanges(log);
};

const formatValue = (value) => {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'object') return JSON.stringify(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

// Watch for page changes
watch(currentPage, () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
});
</script>

<style scoped>
/* Custom scrollbar for modal */
.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
